/**
 * The hooks views use: the services from context, the live session and
 * theme, and the data hooks — each a one-line binding of a `…Query` /
 * `…Mutation` factory (`src/api/`) to this app's backend.
 *
 * Only views (`src/views/`) call these. Presentational components never do:
 * `stories.test.ts` fails a component that imports from `app/`, `api/`
 * (values), `auth/` or TanStack Query.
 */
import { createUlidFactory } from '@acp/domain/ulid.js';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState, useSyncExternalStore } from 'react';
import { meQuery } from '../api/identity.js';
import { createRundownEditorSession, type EditorSession, type EditorSessionDeps, type EditorSnapshot } from '../api/rundownEditor.js';
import { createPlayoutSession, type PlayoutNotice, type PlayoutSession, type PlayoutSessionDeps, type PlayoutSnapshot } from '../api/playout.js';
import { asRunQuery, createProductionMutation, endProductionMutation, playlistsQuery, productionQuery, productionsQuery } from '../api/productions.js';
import { createRundownMutation, rundownsQuery } from '../api/rundowns.js';
import { createStudioMutation, saveStudioMutation, studioQuery, studiosQuery } from '../api/studios.js';
import { type Grants, grantsOf } from '../lib/permissions.js';
import type { Session } from '../auth/session.js';
import type { ConnectivityState } from '../connectivity/state.js';
import type { Theme } from '../lib/theme.js';
import { browserRandomBytes, browserSocket, browserTimers } from './browserPorts.js';
import type { Services } from './services.js';

export const ServicesContext = createContext<Services | null>(null);

export function useServices(): Services {
  const services = useContext(ServicesContext);
  if (services === null) throw new Error('useServices() outside <ServicesContext.Provider> — views render inside startApp()');
  return services;
}

export function useSession(): Session | null {
  const { sessions } = useServices();
  return useSyncExternalStore(sessions.subscribe, sessions.getSession, sessions.getSession);
}

export function useTheme(): Theme {
  const { theme } = useServices();
  return useSyncExternalStore(theme.subscribe, theme.get, theme.get);
}

/** The live connectivity state (PLAN §10.2): every watched endpoint, the primary, the socket. */
export function useConnectivity(): ConnectivityState {
  const { connectivity } = useServices();
  return useSyncExternalStore(connectivity.subscribe, connectivity.getState, connectivity.getState);
}

/** The primary backend's endpoint id: changes only on a failover or the operator's switch (not on every probe). */
export function usePrimaryId(): string {
  const { connectivity } = useServices();
  const primaryId = () => connectivity.getState().primaryId;
  return useSyncExternalStore(connectivity.subscribe, primaryId, primaryId);
}

export function useRundowns() {
  return useQuery(rundownsQuery(useServices().backend.api));
}

export function useCreateRundown() {
  const queryClient = useQueryClient();
  return useMutation(createRundownMutation(useServices().backend.api, queryClient));
}

export function useMe() {
  return useQuery(meQuery(useServices().backend.api));
}

/**
 * The live editor session for one rundown (`api/rundownEditor.ts`), created
 * once per mount and started/stopped with it. Remount (a `key`) for another
 * rundown.
 */
export function useRundownEditorSession(rundownId: string, notify: EditorSessionDeps['notify']): EditorSession {
  const services = useServices();
  const { backend, sessions, connectivity } = services;
  const tenantId = useSession()?.tenantId ?? '';
  const primaryId = usePrimaryId();
  const [session] = useState(() =>
    createRundownEditorSession({
      api: backend.api,
      // Read at every subscribe: the getter names the CURRENT primary.
      wsUrl: () => backend.wsUrl,
      reportSocket: connectivity.reportSocket,
      tenantId,
      rundownId,
      token: () => sessions.idToken(false),
      openSocket: browserSocket,
      timers: browserTimers,
      nowMs: services.nowMs,
      randomBytes: browserRandomBytes,
      notify,
    }),
  );
  useEffect(() => {
    session.start();
    return () => session.stop();
  }, [session]);
  // A failover or the operator's switch: reload from the new primary and resubscribe there.
  useEffect(() => {
    session.setPrimary(primaryId);
  }, [session, primaryId]);
  return session;
}

/** The session's snapshot, re-rendering on every change. */
export function useEditorSnapshot(session: EditorSession): EditorSnapshot {
  return useSyncExternalStore(session.subscribe, session.snapshot, session.snapshot);
}

/** The signed-in user's grants (D14) for hiding and disabling controls; null (unknown) on a node, which serves no `/me`. */
export function useGrants(): Grants {
  return grantsOf(useMe().data);
}

/** The browser's clock, re-read every `intervalMs` (the playout view's countdowns tick on it). */
export function useNow(intervalMs: number): number {
  const { nowMs } = useServices();
  const [now, setNow] = useState(nowMs);
  useEffect(() => browserTimers.every(intervalMs, () => setNow(nowMs())), [intervalMs, nowMs]);
  return now;
}

/** A ULID factory on the browser's clock and randomness (ids the client mints: productions, commands). */
export function useIdFactory(): () => string {
  const { nowMs } = useServices();
  const [newId] = useState(() => createUlidFactory(nowMs, browserRandomBytes));
  return newId;
}

// ─── studios ───

export function useStudios() {
  return useQuery(studiosQuery(useServices().backend.api));
}

export function useStudio(studioId: string) {
  return useQuery(studioQuery(useServices().backend.api, studioId));
}

export function useCreateStudio() {
  const queryClient = useQueryClient();
  return useMutation(createStudioMutation(useServices().backend.api, queryClient));
}

export function useSaveStudio() {
  const queryClient = useQueryClient();
  return useMutation(saveStudioMutation(useServices().backend.api, queryClient));
}

// ─── productions ───

export function useProductions() {
  return useQuery(productionsQuery(useServices().backend.api));
}

export function useProduction(productionId: string) {
  return useQuery(productionQuery(useServices().backend.api, productionId));
}

export function usePlaylists() {
  return useQuery(playlistsQuery(useServices().backend.api));
}

export function useCreateProduction() {
  const queryClient = useQueryClient();
  const newId = useIdFactory();
  return useMutation(createProductionMutation(useServices().backend.api, queryClient, newId));
}

export function useEndProduction() {
  const queryClient = useQueryClient();
  return useMutation(endProductionMutation(useServices().backend.api, queryClient));
}

export function useAsRun(productionId: string) {
  return useInfiniteQuery(asRunQuery(useServices().backend.api, productionId));
}

/**
 * The live playout session for one production (`api/playout.ts`), created
 * once per mount and started/stopped with it. Remount (a `key`) for another
 * production.
 */
/** The ports a playout session takes, from the app's services (the browser's socket and timers). */
export function playoutSessionDeps(services: Services, tenantId: string, productionId: string, newId: () => string, notify: (notice: PlayoutNotice) => void): PlayoutSessionDeps {
  const { backend, sessions, connectivity } = services;
  return {
    api: backend.api,
    // Read at every subscribe: the getter names the CURRENT primary.
    wsUrl: () => backend.wsUrl,
    reportSocket: connectivity.reportSocket,
    tenantId,
    productionId,
    token: () => sessions.idToken(false),
    openSocket: browserSocket,
    timers: browserTimers,
    nowMs: services.nowMs,
    newCommandId: () => `web-${newId()}`,
    notify,
  };
}

/**
 * The live playout session for one production (`api/playout.ts`), created
 * once per mount and started/stopped with it. Remount (a `key`) for another
 * production.
 */
export function usePlayoutSession(productionId: string, notify: (notice: PlayoutNotice) => void): PlayoutSession {
  const services = useServices();
  const tenantId = useSession()?.tenantId ?? '';
  const primaryId = usePrimaryId();
  const newId = useIdFactory();
  const [session] = useState(() => createPlayoutSession(playoutSessionDeps(services, tenantId, productionId, newId, notify)));
  useEffect(() => {
    session.start();
    return () => session.stop();
  }, [session]);
  useEffect(() => {
    session.setPrimary(primaryId);
  }, [session, primaryId]);
  return session;
}

export function usePlayoutSnapshot(session: PlayoutSession): PlayoutSnapshot {
  return useSyncExternalStore(session.subscribe, session.snapshot, session.snapshot);
}
